import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Drawer,
  Empty,
  Form,
  Input,
  List,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Timeline,
  Tooltip,
  Typography,
  Upload,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  FileOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import AppraisalFiveR from "./AppraisalFiveR";

const { Text, Title } = Typography;

const COMPONENTS = [
  {
    value: "INSTRUCTIONAL_SYLLABUS",
    label: "Instructional & Syllabus Coverage",
  },
  { value: "DISCIPLINE", label: "Discipline" },
  { value: "CREATIVITY", label: "Creativity & Co-curricular" },
];

const SNAPSHOT_GROUPS = [
  {
    key: "instructional",
    title: "Instructional & Syllabus Coverage",
    fields: [
      ["Materials planned", "materialsPlanned"],
      ["Materials delivered", "materialsDelivered"],
      ["Materials approved", "materialsApproved"],
      ["Coverage", "coveragePercentage", "%"],
      ["Readiness score", "readinessScore", "/100"],
    ],
  },  {
    key: "discipline",
    title: "Discipline",
    fields: [
      ["Total incidents", "totalIncidents"],
      ["Resolved incidents", "resolvedCount"],
      ["Open incidents", "openCount"],
      ["Last incident", "lastIncidentDate", "date"],
      ["Deduction applied", "deductionApplied"],
      ["Discipline score", "disciplineScore", "/100"],
    ],
  },
  {
    key: "curricular",
    title: "Creativity & Co-curricular",
    fields: [
      ["Events coordinated", "eventsCoordinated"],
      ["Events with participation recorded", "eventsWithParticipationRecorded"],
      ["Creativity score", "creativityScore", "/100"],
    ],
  },
];

const MATERIAL_HELP = {
  materialsPlanned:
    "All instructional materials the staff uploaded for the term.",
  materialsDelivered:
    "The HoD-confirmed delivered subset of unlocked materials.",
  materialsApproved:
    "The subset of planned materials whose approval action is Approved.",
  materialsUnlocked:
    "Materials unlocked and available to learners for the term.",
};

const snapshotLabel = (label, key) =>
  MATERIAL_HELP[key] ? (
    <Tooltip title={MATERIAL_HELP[key]}>
      <span style={{ textDecoration: "underline dotted", cursor: "help" }}>
        {label}
      </span>
    </Tooltip>
  ) : (
    label
  );

const getErrorMessage = (error) =>
  error?.response?.data?.detail || error?.message || "Something went wrong.";

const formatDateTime = (value) =>
  value && dayjs(value).isValid()
    ? dayjs(value).format("DD MMM YYYY, HH:mm")
    : "—";

const formatValue = (value, suffix) => {
  if (value === null || value === undefined || value === "") return "—";
  if (suffix === "date") {
    return dayjs(value).isValid() ? dayjs(value).format("DD MMM YYYY") : "—";
  }
  return `${value}${suffix || ""}`;
};

const safeEvidenceUrl = (value) => {
  if (typeof value !== "string" || !value.trim()) return null;
  const url = value.trim();
  if (/^https?:\/\//i.test(url)) return url;
  if (/^\/(?!\/)/.test(url) || /^\.\.?\//.test(url)) return url;
  return null;
};

const toSnakeKey = (key) =>
  String(key || "").replace(/([A-Z])/g, (m) => `_${m.toLowerCase()}`);

const getSnapshotValue = (item, camelKey) => {
  if (!item) return null;
  const direct = item[camelKey];
  if (direct !== undefined && direct !== null && direct !== "") return direct;
  const fallback = item[toSnakeKey(camelKey)];
  if (fallback !== undefined && fallback !== null && fallback !== "")
    return fallback;
  return direct ?? fallback ?? null;
};

const getSnapshotTermName = (item) =>
  item?.termName ?? item?.term_name ?? item?.term ?? "Term not specified";

const getSnapshotComputedAt = (item) =>
  item?.computedAt ?? item?.computed_at ?? null;

const getComputationNotes = (obj) => {
  if (!obj || typeof obj !== "object") return null;
  const notes =
    obj.computationNotes ?? obj.computation_notes ?? obj.notes ?? null;
  if (notes === null || notes === undefined || notes === "") return null;
  return String(notes);
};

const formatSeverityMap = (value) => {
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    return value
      .map((entry) => {
        if (typeof entry === "string") return entry;
        if (entry && typeof entry === "object") {
          const level = entry.level ?? entry.severity ?? entry.name ?? "";
          const count = entry.count ?? entry.num ?? entry.n ?? "";
          const each = entry.each ?? entry.deduction ?? entry.points ?? "";
          if (level && count)
            return `${level} x${count}${each ? ` (-${each} each)` : ""}`;
          try {
            return JSON.stringify(entry);
          } catch (_) {
            return String(entry);
          }
        }
        return String(entry);
      })
      .join("; ");
  }
  if (value && typeof value === "object") {
    const parts = Object.entries(value)
      .filter(([, num]) => num !== null && num !== undefined && Number(num) > 0)
      .map(([level, num]) => `${level} x${num}`);
    if (parts.length) return parts.join("; ");
    try {
      return JSON.stringify(value);
    } catch (_) {
      return String(value);
    }
  }
  return String(value);
};

const getDisciplineMeta = (item) => {
  const notes = getComputationNotes(item);
  const explicitSeverity =
    item?.severityBreakdown ??
    item?.severity_breakdown ??
    item?.severityDetail ??
    item?.severity_detail ??
    null;
  let severityText = null;
  if (
    explicitSeverity !== null &&
    explicitSeverity !== undefined &&
    explicitSeverity !== ""
  ) {
    severityText = formatSeverityMap(explicitSeverity);
  } else if (notes) {
    const closedMatch = notes.match(/Closed counted:\s*[^|]+/);
    if (closedMatch) severityText = closedMatch[0].trim();
    else {
      const parts = notes.match(/\b[a-z]+\s*x\d+\s*\([^)]*each\)[^|;]*/gi);
      if (parts && parts.length) severityText = parts.join("; ").trim();
    }
  }
  let path = null;
  const explicitFormula =
    item?.formula ?? item?.formulaUsed ?? item?.formula_used ?? null;
  const explicitFallback =
    item?.fallback ?? item?.usedFallback ?? item?.isFallback ?? null;
  if (explicitFormula) path = "Formula";
  else if (
    explicitFallback === true ||
    (typeof explicitFallback === "string" && explicitFallback.length)
  )
    path = "Fallback";
  else if (notes) {
    if (/\|\s*Fallback:/.test(notes) || /Fallback:\s*max\(100/.test(notes))
      path = "Fallback";
    else if (/\|\s*Formula:/.test(notes)) path = "Formula";
  }
  return { notes, severityText, path };
};

const getInstructionalUnlocked = (item) => {
  if (!item || typeof item !== "object") return null;
  const unlocked =
    item.materialsUnlocked ??
    item.materials_unlocked ??
    item.unlockedCount ??
    item.unlocked_count ??
    item.unlocked ??
    null;
  if (unlocked === undefined || unlocked === null || unlocked === "")
    return null;
  return unlocked;
};

const renderScoreNotes = (value, score) => {
  const raw = value ?? score?.computation_notes ?? score?.notes ?? null;
  if (raw === null || raw === undefined || raw === "") return "—";
  const text = String(raw);
  return (
    <Tooltip title={<span style={{ whiteSpace: "pre-wrap" }}>{text}</span>}>
      <span style={{ wordBreak: "break-word" }}>{text}</span>
    </Tooltip>
  );
};

const getFiveRRawScore = (record, componentScores) => {
  const fiveR = record?.fiveR;
  const fromFiveR =
    fiveR?.rawScore ??
    fiveR?.raw_score ??
    fiveR?.raw ??
    fiveR?.normalizedScore ??
    fiveR?.normalized_score ??
    fiveR?.normalized ??
    null;
  if (fromFiveR !== null && fromFiveR !== undefined && fromFiveR !== "")
    return fromFiveR;
  const annual = (Array.isArray(componentScores) ? componentScores : []).find(
    (entry) => entry?.component === "SUPERVISOR_5R",
  );
  const fromScore = annual?.rawScore ?? annual?.raw_score ?? null;
  if (fromScore !== null && fromScore !== undefined && fromScore !== "")
    return fromScore;
  return null;
};

const getFiveRAvg = (record) =>
  record?.fiveR?.averageRating ?? record?.fiveR?.average_rating ?? null;

const termLabel = (term) =>
  term?.name ||
  term?.termName ||
  term?.title ||
  term?.acad_term ||
  "Unnamed term";

const NoteFormModal = ({
  open,
  note,
  recordId,
  terms,
  request,
  onCancel,
  onSaved,
}) => {
  const [form] = Form.useForm();
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      entryType: note?.entryType || "Feedback",
      component: note?.component || undefined,
      category: note?.category || "",
      comment: note?.comment || "",
      termId: undefined,
    });
    setFile(null);
    setError("");
  }, [form, note, open]);

  const save = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (_) {
      return;
    }

    setSaving(true);
    setError("");
    try {
      const payload = {
        appraisalRecordId: recordId,
        entryType: values.entryType,
        component: values.component || "",
        category: values.category || "",
        comment: values.comment || "",
      };
      if (values.termId !== undefined) payload.termId = values.termId;

      let body = payload;
      if (file) {
        body = new FormData();
        Object.entries(payload).forEach(([key, value]) =>
          body.append(key, value),
        );
        body.append("evidenceFile", file);
      }

      await request(
        note ? "PATCH" : "POST",
        note ? `notes/${note.id}` : "notes",
        body,
      );
      await onSaved();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={note ? "Edit supervisor note" : "Add supervisor note"}
      okText={note ? "Save changes" : "Add note"}
      onOk={save}
      onCancel={() => {
        if (!saving) onCancel();
      }}
      confirmLoading={saving}
      cancelButtonProps={{ disabled: saving }}
      closable={!saving}
      maskClosable={!saving}
      destroyOnClose
    >
      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      <Form form={form} layout="vertical">
        <Form.Item
          name="entryType"
          label="Entry type"
          rules={[{ required: true }]}
        >
          <Select
            options={[
              { value: "Feedback", label: "Feedback" },
              { value: "Exception", label: "Exception" },
            ]}
          />
        </Form.Item>
        <Form.Item
          name="termId"
          label={note?.termName ? `Term (currently ${note.termName})` : "Term"}
        >
          <Select
            allowClear
            placeholder={
              note?.termName
                ? "Leave unchanged or select a replacement"
                : "All terms / not specified"
            }
            options={(Array.isArray(terms) ? terms : terms?.results || [])
              .filter((term) => term?.id != null)
              .map((term) => ({ value: term.id, label: termLabel(term) }))}
          />
        </Form.Item>
        <Form.Item name="component" label="Component">
          <Select
            allowClear
            placeholder="Not component-specific"
            options={COMPONENTS}
          />
        </Form.Item>
        <Form.Item
          name="category"
          label="Category"
          dependencies={["entryType"]}
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (
                  getFieldValue("entryType") !== "Exception" ||
                  value?.trim()
                ) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error("Category is required for an exception."),
                );
              },
            }),
          ]}
        >
          <Input placeholder="For example, illness or exceptional achievement" />
        </Form.Item>
        <Form.Item
          name="comment"
          label="Comment"
          dependencies={["entryType"]}
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (
                  getFieldValue("entryType") !== "Feedback" ||
                  value?.trim()
                ) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error("Comment is required for feedback."),
                );
              },
            }),
          ]}
        >
          <Input.TextArea
            rows={4}
            placeholder="Add the feedback or supporting context"
          />
        </Form.Item>
        <Form.Item label="Evidence file">
          <Upload
            beforeUpload={(selected) => {
              setFile(selected);
              return false;
            }}
            onRemove={() => setFile(null)}
            fileList={file ? [file] : []}
            maxCount={1}
          >
            <Button icon={<UploadOutlined />}>Choose file</Button>
          </Upload>
          {note?.evidenceFile && !file && (
            <Text type="secondary">
              {" "}
              Saving without a new file keeps the existing evidence.
            </Text>
          )}
        </Form.Item>
      </Form>
    </Modal>
  );
};

const AppraisalRecordDrawer = ({
  recordId,
  onClose,
  request,
  canManageNotes,
  canManageFiveR = false,
  cycleId,
  cycleLocked = false,
  terms = [],
  onChanged,
}) => {
  const [record, setRecord] = useState(null);
  const [snapshots, setSnapshots] = useState({});
  const [trail, setTrail] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [noteError, setNoteError] = useState("");
  const [noteModal, setNoteModal] = useState({ open: false, note: null });
  const [deletingNoteId, setDeletingNoteId] = useState(null);
  const loadVersion = useRef(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      loadVersion.current += 1;
    };
  }, []);

  const load = useCallback(async () => {
    if (!recordId) return;
    const version = ++loadVersion.current;
    setLoading(true);
    setError("");
    try {
      const [recordData, snapshotData, approvalData] = await Promise.all([
        request("GET", `records/${recordId}`),
        request("GET", `records/${recordId}/snapshots`),
        request("GET", `records/${recordId}/approval-steps`),
      ]);
      if (!mounted.current || version !== loadVersion.current) return;
      setRecord(recordData);
      setSnapshots(snapshotData || {});
      setTrail(
        Array.isArray(approvalData)
          ? approvalData
          : approvalData?.results || [],
      );
      return true;
    } catch (err) {
      if (mounted.current && version === loadVersion.current)
        setError(getErrorMessage(err));
      return false;
    } finally {
      if (mounted.current && version === loadVersion.current) setLoading(false);
    }
  }, [recordId, request]);

  useEffect(() => {
    loadVersion.current += 1;
    setRecord(null);
    setSnapshots({});
    setTrail([]);
    setError("");
    setNoteError("");
    setNoteModal({ open: false, note: null });
    setDeletingNoteId(null);
    if (recordId) load();
    else setLoading(false);
  }, [load, recordId]);

  const noteSaved = async () => {
    await load();
    if (!mounted.current) return;
    setNoteModal({ open: false, note: null });
    if (onChanged) onChanged();
  };

  const fiveRChanged = async () => {
    const refreshed = await load();
    if (mounted.current) onChanged?.();
    return refreshed === true;
  };

  const deleteNote = async (noteId) => {
    setDeletingNoteId(noteId);
    setNoteError("");
    try {
      await request("DELETE", `notes/${noteId}`);
      await load();
      if (mounted.current && onChanged) onChanged();
    } catch (err) {
      if (mounted.current) setNoteError(getErrorMessage(err));
    } finally {
      if (mounted.current) setDeletingNoteId(null);
    }
  };

  const notes = Array.isArray(record?.supervisorNotes)
    ? record.supervisorNotes
    : [];
  const componentScores = Array.isArray(record?.componentScores)
    ? record.componentScores
    : [];
  const fiveRRawScore = getFiveRRawScore(record, componentScores);
  const fiveRAvg = getFiveRAvg(record);

  const closeDrawer = () => {
    setNoteModal({ open: false, note: null });
    setNoteError("");
    onClose();
  };

  return (
    <Drawer
      open={Boolean(recordId)}
      onClose={closeDrawer}
      width={960}
      title={
        record ? `${record.staffName} — Appraisal record` : "Appraisal record"
      }
      destroyOnClose={false}
    >
      {loading && !record ? (
        <div style={{ padding: 64, textAlign: "center" }}>
          <Spin size="large" />
        </div>
      ) : error && !record ? (
        <Alert
          type="error"
          message="Unable to load appraisal record"
          description={error}
          showIcon
          action={<Button onClick={load}>Retry</Button>}
        />
      ) : record ? (
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          {error && (
            <Alert
              type="error"
              message={error}
              showIcon
              closable
              onClose={() => setError("")}
            />
          )}
          <Card size="small">
            <Row gutter={[16, 16]}>
              <Col xs={12} md={6}>
                <Statistic
                  title={
                    record.fiveRComplete === true
                      ? "Performance index"
                      : "Performance index (provisional)"
                  }
                  value={record.apiScore ?? "—"}
                  suffix={record.apiScore == null ? "" : "/100"}
                  precision={record.apiScore == null ? undefined : 2}
                />
              </Col>
              <Col xs={12} md={6}>
                <Statistic title="Grade" value={record.apiGrade || "—"} />
              </Col>
              <Col xs={12} md={6}>
                <Statistic title="Status" value={record.statusDisplay || "—"} />
              </Col>
              <Col xs={12} md={6}>
                <Statistic
                  title="Flagged"
                  value={record.isFlagged ? "Yes" : "No"}
                />
              </Col>
            </Row>
            <Divider />
            <Descriptions size="small" column={{ xs: 1, sm: 2 }}>
              <Descriptions.Item label="Staff member">
                {record.staffName || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Role at appraisal">
                {record.roleSnapshot || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Staff pathway">
                <Tag
                  color={
                    record.staffCategory === "teaching" ? "blue" : "purple"
                  }
                >
                  {record.staffCategoryDisplay ||
                    (record.staffCategory === "teaching"
                      ? "Teaching"
                      : record.staffCategory === "non_teaching"
                        ? "Non-Teaching"
                        : "—")}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Cycle">
                {record.cycleName || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Score last updated">
                {formatDateTime(record.lastScoreUpdatedAt)}
              </Descriptions.Item>
              {record.isFlagged && (
                <Descriptions.Item label="Flag reason" span={2}>
                  {record.flagReason || "—"}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Promotion recommended">
                {record.promotionFlag ? "Yes" : "No"}
              </Descriptions.Item>
              <Descriptions.Item label="Training recommended">
                {record.trainingFlag ? "Yes" : "No"}
              </Descriptions.Item>
              <Descriptions.Item label="Final remarks" span={2}>
                {record.finalRemarks || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Approved by">
                {record.approvedBy || "—"}
              </Descriptions.Item>
              <Descriptions.Item label="Approved at">
                {formatDateTime(record.approvedAt)}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {record.fiveRComplete !== true && (
            <Alert
              type="info"
              showIcon
              message="Annual supervisor 5R assessment pending"
              description="The index is provisional until the HOD submits the annual assessment. A missing assessment is not scored as zero."
            />
          )}
          {fiveRRawScore !== null &&
            fiveRRawScore !== undefined &&
            fiveRRawScore !== "" && (
              <Text type="secondary">
                {fiveRAvg !== null &&
                fiveRAvg !== undefined &&
                fiveRAvg !== ""
                  ? `Raw ${fiveRRawScore} / Avg ${fiveRAvg}`
                  : `Raw ${fiveRRawScore}`}
              </Text>
            )}
          <AppraisalFiveR
            record={record}
            request={request}
            canManage={canManageFiveR}
            cycleId={cycleId}
            cycleLocked={cycleLocked}
            onChanged={fiveRChanged}
          />

          <section>
            <Title level={4}>Computed component scores</Title>
            <Text type="secondary">
              System components are scored per term. Supervisor 5R contributes
              once per year. Earlier component rows retain their original
              weights.
            </Text>
            <Table
              size="small"
              rowKey={(score) =>
                score.id ||
                `${score.termName ?? score.term_name ?? score.term ?? "term"}-${score.component}`
              }
              pagination={false}
              scroll={{ x: 900 }}
              locale={{ emptyText: "No computed component scores available" }}
              dataSource={componentScores}
              columns={[
                {
                  title: "Term",
                  dataIndex: "termName",
                  render: (value, score) => {
                    const term =
                      value ?? score?.term_name ?? score?.term ?? null;
                    if (
                      score.component === "SUPERVISOR_5R" &&
                      (term == null || term === "")
                    )
                      return "Annual";
                    return term || "—";
                  },
                },
                {
                  title: "Component",
                  dataIndex: "componentDisplay",
                  render: (value, score) =>
                    value ||
                    COMPONENTS.find(
                      (component) => component.value === score.component,
                    )?.label ||
                    (score.component === "SUPERVISOR_5R"
                      ? "Supervisor 5R Assessment"
                      : "—"),
                },
                {
                  title: "Raw score",
                  dataIndex: "rawScore",
                  render: (value, score) =>
                    formatValue(value ?? score?.raw_score, "/100"),
                },
                {
                  title: "Weight",
                  dataIndex: "weightSnapshot",
                  render: (value, score) =>
                    formatValue(value ?? score?.weight_snapshot, "%"),
                },
                {
                  title: "Weighted score",
                  dataIndex: "weightedScore",
                  render: (value, score) =>
                    formatValue(value ?? score?.weighted_score),
                },
                {
                  title: "Locked",
                  dataIndex: "isLocked",
                  render: (value, score) => {
                    const locked = value ?? score?.is_locked ?? false;
                    return (
                      <Tag color={locked ? "default" : "green"}>
                        {locked ? "Locked" : "Open"}
                      </Tag>
                    );
                  },
                },
                {
                  title: "Computation notes",
                  dataIndex: "computationNotes",
                  width: 280,
                  render: (value, score) => renderScoreNotes(value, score),
                },
                {
                  title: "Last computed",
                  dataIndex: "lastComputedAt",
                  render: (value, score) =>
                    formatDateTime(value ?? score?.last_computed_at),
                },
              ]}
            />
          </section>

          <section>
            <Title level={4}>Scores by term</Title>
            {SNAPSHOT_GROUPS.map((group) => {
              const items = Array.isArray(snapshots[group.key])
                ? snapshots[group.key]
                : [];
              const staffPathway =
                record.staffCategory ?? record.staff_category ?? null;
              if (
                staffPathway === "non_teaching" &&
                group.key !== "discipline" &&
                !items.length
              )
                return null;
              return (
                <Card
                  key={group.key}
                  title={group.title}
                  size="small"
                  style={{ marginBottom: 12 }}
                >
                  {items.length ? (
                    items.map((item, index) => {
                      const disciplineMeta =
                        group.key === "discipline"
                          ? getDisciplineMeta(item)
                          : null;
                      const snapshotNotes =
                        group.key === "discipline"
                          ? null
                          : getComputationNotes(item);
                      const unlocked =
                        group.key === "instructional"
                          ? getInstructionalUnlocked(item)
                          : null;
                      const delivered =
                        group.key === "instructional"
                          ? getSnapshotValue(item, "materialsDelivered")
                          : null;
                      return (
                        <div
                          key={`${getSnapshotTermName(item) || "term"}-${index}`}
                        >
                          {index > 0 && <Divider />}
                          <Space
                            direction="vertical"
                            style={{ width: "100%" }}
                          >
                            <Text strong>
                              {getSnapshotTermName(item)}
                            </Text>
                            <Descriptions
                              size="small"
                              column={{ xs: 1, sm: 2, md: 3 }}
                            >
                              {group.fields.map(([label, key, suffix]) => (
                                <Descriptions.Item
                                  key={key}
                                  label={snapshotLabel(label, key)}
                                >
                                  {formatValue(
                                    getSnapshotValue(item, key),
                                    suffix,
                                  )}
                                </Descriptions.Item>
                              ))}
                              {group.key === "instructional" &&
                                unlocked !== null && (
                                  <Descriptions.Item
                                    label={snapshotLabel(
                                      "Materials unlocked",
                                      "materialsUnlocked",
                                    )}
                                  >
                                    {formatValue(unlocked)}
                                  </Descriptions.Item>
                                )}
                              <Descriptions.Item label="Computed at">
                                {formatDateTime(getSnapshotComputedAt(item))}
                              </Descriptions.Item>
                            </Descriptions>
                            {group.key === "instructional" &&
                              unlocked !== null &&
                              delivered !== null &&
                              delivered !== "" && (
                                <Text type="secondary">
                                  {`Delivered (HoD-confirmed) ${delivered} over unlocked ${unlocked} — coverage is delivered/unlocked.`}
                                </Text>
                              )}
                            {group.key === "discipline" &&
                              (disciplineMeta.notes ||
                                disciplineMeta.severityText ||
                                disciplineMeta.path) && (
                                <Space
                                  direction="vertical"
                                  size={4}
                                  style={{ width: "100%" }}
                                >
                                  {(disciplineMeta.severityText ||
                                    disciplineMeta.path) && (
                                    <Space wrap size={8}>
                                      {disciplineMeta.path && (
                                        <Tag
                                          color={
                                            disciplineMeta.path === "Formula"
                                              ? "blue"
                                              : "default"
                                          }
                                        >
                                          {disciplineMeta.path}
                                        </Tag>
                                      )}
                                      {disciplineMeta.severityText && (
                                        <Text type="secondary">
                                          {disciplineMeta.severityText}
                                        </Text>
                                      )}
                                    </Space>
                                  )}
                                  {disciplineMeta.notes && (
                                    <Text
                                      type="secondary"
                                      style={{ whiteSpace: "pre-wrap" }}
                                    >
                                      {disciplineMeta.notes}
                                    </Text>
                                  )}
                                </Space>
                              )}
                            {(group.key === "curricular" ||
                              group.key === "instructional") &&
                              snapshotNotes && (
                                <Text
                                  type="secondary"
                                  style={{ whiteSpace: "pre-wrap" }}
                                >
                                  {snapshotNotes}
                                </Text>
                              )}
                          </Space>
                        </div>
                      );
                    })
                  ) : (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="No source snapshot available"
                    />
                  )}
                </Card>
              );
            })}
          </section>

          <section>
            <Space style={{ width: "100%", justifyContent: "space-between" }}>
              <Title level={4} style={{ margin: 0 }}>
                Supervisor notes
              </Title>
              {canManageNotes && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setNoteModal({ open: true, note: null })}
                >
                  Add note
                </Button>
              )}
            </Space>
            {noteError && (
              <Alert
                type="error"
                message={noteError}
                showIcon
                closable
                onClose={() => setNoteError("")}
                style={{ marginTop: 12 }}
              />
            )}
            <List
              style={{ marginTop: 8 }}
              locale={{ emptyText: "No supervisor notes" }}
              dataSource={notes}
              renderItem={(note) => {
                const evidenceUrl = safeEvidenceUrl(note.evidenceFile);
                return (
                  <List.Item
                    actions={
                      canManageNotes
                        ? [
                            <Button
                              key="edit"
                              type="text"
                              icon={<EditOutlined />}
                              onClick={() => setNoteModal({ open: true, note })}
                            >
                              Edit
                            </Button>,
                            <Popconfirm
                              key="delete"
                              title="Delete this note?"
                              description="This cannot be undone."
                              onConfirm={() => deleteNote(note.id)}
                              okButtonProps={{
                                loading: deletingNoteId === note.id,
                              }}
                            >
                              <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                loading={deletingNoteId === note.id}
                              >
                                Delete
                              </Button>
                            </Popconfirm>,
                          ]
                        : undefined
                    }
                  >
                    <List.Item.Meta
                      title={
                        <Space wrap>
                          <Tag
                            color={
                              note.entryType === "Exception" ? "orange" : "blue"
                            }
                          >
                            {note.entryType}
                          </Tag>
                          {note.componentDisplay && (
                            <Tag>{note.componentDisplay}</Tag>
                          )}
                          {note.termName && <Text>{note.termName}</Text>}
                        </Space>
                      }
                      description={
                        <Space direction="vertical" size={2}>
                          {note.category && <Text strong>{note.category}</Text>}
                          <Text>{note.comment || "No comment provided."}</Text>
                          <Text type="secondary">
                            By {note.supervisorName || "Unknown supervisor"} ·{" "}
                            {formatDateTime(note.createdAt)}
                          </Text>
                          {evidenceUrl && (
                            <Typography.Link
                              href={evidenceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <FileOutlined /> View evidence
                            </Typography.Link>
                          )}
                        </Space>
                      }
                    />
                  </List.Item>
                );
              }}
            />
          </section>

          <section>
            <Title level={4}>Approval trail</Title>
            {trail.length ? (
              <Timeline
                items={trail.map((step) => ({
                  color:
                    step.action === "Returned"
                      ? "red"
                      : step.action === "Reopened"
                        ? "orange"
                        : "green",
                  children: (
                    <Space direction="vertical" size={2}>
                      <Space wrap>
                        <Text strong>{step.actionDisplay || step.action}</Text>
                        <Tag>{step.stageDisplay || "Review stage"}</Tag>
                      </Space>
                      <Text>
                        {step.actorName || "Unknown reviewer"} ·{" "}
                        {formatDateTime(step.actedAt)}
                      </Text>
                      <Text>
                        Recorded score:{" "}
                        {formatValue(step.snapshotApiScore, "/100")}
                      </Text>
                      {step.remarks && (
                        <Text type="secondary">{step.remarks}</Text>
                      )}
                    </Space>
                  ),
                }))}
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No approval activity yet"
              />
            )}
          </section>
        </Space>
      ) : null}

      <NoteFormModal
        open={noteModal.open}
        note={noteModal.note}
        recordId={recordId}
        terms={terms}
        request={request}
        onCancel={() => setNoteModal({ open: false, note: null })}
        onSaved={noteSaved}
      />
    </Drawer>
  );
};

export default AppraisalRecordDrawer;
